import type {
  CapturedFrame,
  EncodedVideoChunkLike,
  VideoEncodeConfig,
  VideoEncoderAdapter,
  VideoEncoderState,
} from '../../visual/index.js';
import type { CanvasCapturedFrame } from './CanvasFrameSource.js';

/**
 * `VideoEncoder`, behind the adapter the core holds.
 *
 * Everything browser-specific about encoding is in this file: the global, the
 * config shape, the callback pair, the queue counter. `VideoExportSession` sees
 * `configure / encode / flush / close / queueSize` and could be handed a fake —
 * which is what the tests do, and it is why none of them need a browser.
 *
 * ## Errors
 *
 * `VideoEncoder` reports failures through a callback rather than by throwing, so
 * a failed encode surfaces on a later turn than the `encode()` that caused it.
 * That is caught here and turned into a state a caller can see, because the
 * alternative is an export that quietly produces nothing and reports success.
 */

interface EncodedChunkNative {
  readonly type: 'key' | 'delta';
  readonly timestamp: number;
  readonly duration: number | null;
  readonly byteLength: number;
  copyTo(destination: ArrayBufferView): void;
}

interface VideoEncoderNative {
  readonly state: 'unconfigured' | 'configured' | 'closed';
  readonly encodeQueueSize: number;
  configure(config: Record<string, unknown>): void;
  encode(frame: unknown, options?: { keyFrame?: boolean }): void;
  flush(): Promise<void>;
  close(): void;
  reset(): void;
}

interface VideoEncoderConstructor {
  new (init: {
    output: (chunk: EncodedChunkNative, metadata?: unknown) => void;
    error: (error: Error) => void;
  }): VideoEncoderNative;
  isConfigSupported(config: Record<string, unknown>): Promise<{ supported?: boolean }>;
}

function encoderGlobal(): VideoEncoderConstructor | undefined {
  // Through unknown: the DOM lib already declares a VideoEncoder whose
  // signatures are narrower than the structural type used here, and the two do
  // not overlap enough for a direct assertion.
  return (globalThis as unknown as { VideoEncoder?: VideoEncoderConstructor }).VideoEncoder;
}

/** Whether this browser can encode video at all. */
export function webCodecsAvailable(): boolean {
  return typeof encoderGlobal() === 'function' &&
    typeof (globalThis as { VideoFrame?: unknown }).VideoFrame === 'function';
}

/**
 * Asks the browser whether it could encode with this configuration.
 *
 * Wrapped so a browser that throws on an unknown codec string — which several
 * do — reads as "no" rather than taking the selection down with it.
 */
export async function probeVideoConfig(config: VideoEncodeConfig): Promise<boolean> {
  const Encoder = encoderGlobal();
  if (Encoder === undefined) return false;
  try {
    const answer = await Encoder.isConfigSupported(toNativeConfig(config));
    return answer.supported === true;
  } catch {
    return false;
  }
}

function toNativeConfig(config: VideoEncodeConfig): Record<string, unknown> {
  return {
    codec: config.codec,
    width: config.width,
    height: config.height,
    framerate: config.framerate,
    bitrate: config.bitrate,
    ...(config.hardwareAcceleration === undefined
      ? {}
      : { hardwareAcceleration: config.hardwareAcceleration }),
    ...(config.latencyMode === undefined ? {} : { latencyMode: config.latencyMode }),
    // Annex B is what a raw H.264 stream needs to be useful without a
    // container, and it stays the default so Phase 7A's chunk-level checks keep
    // working. A muxed export overrides it — see `bitstreamFormat`.
    ...(config.codec.startsWith('avc1')
      ? { avc: { format: config.bitstreamFormat ?? 'annexb' } }
      : {}),
  };
}

export interface WebCodecsVideoEncoderOptions {
  /** Receives every chunk, in order. */
  readonly onChunk: (chunk: EncodedVideoChunkLike) => void | Promise<void>;
  readonly onError?: (message: string) => void;
}

export class WebCodecsVideoEncoder implements VideoEncoderAdapter {
  private encoder: VideoEncoderNative | null = null;
  private failure: string | null = null;
  private closed = false;
  private chunks = 0;
  private bytes = 0;
  private decoderConfig: unknown = undefined;

  constructor(private readonly options: WebCodecsVideoEncoderOptions) {}

  get state(): VideoEncoderState {
    if (this.failure !== null) return 'errored';
    if (this.closed || this.encoder === null) return this.encoder === null ? 'unconfigured' : 'closed';
    return this.encoder.state === 'configured' ? 'configured' : 'unconfigured';
  }

  get queueSize(): number {
    return this.encoder?.encodeQueueSize ?? 0;
  }

  /** Chunks emitted so far. Zero means the pipeline is still filling. */
  get producedChunks(): number {
    return this.chunks;
  }

  get lastError(): string | null {
    return this.failure;
  }

  async configure(config: VideoEncodeConfig): Promise<void> {
    const Encoder = encoderGlobal();
    if (Encoder === undefined) {
      throw new Error('WebCodecs VideoEncoder is not available in this browser');
    }

    // Asked before configuring rather than after failing. `configure` on an
    // unsupported config throws asynchronously in some engines and synchronously
    // in others, and the message is rarely about the codec.
    const supported = await probeVideoConfig(config);
    if (!supported) {
      throw new Error(`codec ${config.codec} is not supported at ${config.width}x${config.height}`);
    }

    this.failure = null;
    this.closed = false;
    this.encoder = new Encoder({
      output: (chunk, metadata) => {
        this.chunks += 1;
        this.bytes += chunk.byteLength;
        // The parameter sets arrive once, with the first chunk. A muxer that
        // never sees them writes a track no decoder can start.
        const supplied = (metadata as { decoderConfig?: unknown } | undefined)?.decoderConfig;
        if (supplied !== undefined) this.decoderConfig = supplied;
        void this.options.onChunk(wrapChunk(chunk, this.decoderConfig));
      },
      error: (error) => {
        this.failure = error.message;
        this.options.onError?.(error.message);
      },
    });
    this.encoder.configure(toNativeConfig(config));
  }

  encode(frame: CapturedFrame, options: { readonly keyFrame: boolean }): void {
    if (this.failure !== null) throw new Error(this.failure);
    const encoder = this.encoder;
    if (encoder === null) throw new Error('encoder is not configured');
    const native = (frame as CanvasCapturedFrame).native;
    if (native === undefined) throw new Error('frame did not come from this host');
    encoder.encode(native, { keyFrame: options.keyFrame });
  }

  async flush(): Promise<void> {
    const encoder = this.encoder;
    if (encoder === null || this.closed) return;
    try {
      await encoder.flush();
    } catch (cause) {
      // A flush on an encoder that already failed rejects with the original
      // error. Recorded rather than rethrown, so teardown still completes.
      this.failure = cause instanceof Error ? cause.message : String(cause);
    }
  }

  close(): void {
    const encoder = this.encoder;
    this.closed = true;
    if (encoder === null) return;
    try {
      if (encoder.state !== 'closed') encoder.close();
    } catch {
      // Already closed, or closed by the error path. Nothing to do.
    }
    this.encoder = null;
  }

  diagnostics(): {
    readonly state: VideoEncoderState;
    readonly queueSize: number;
    readonly chunks: number;
    readonly bytes: number;
    readonly error: string | null;
  } {
    return {
      state: this.state,
      queueSize: this.queueSize,
      chunks: this.chunks,
      bytes: this.bytes,
      error: this.failure,
    };
  }
}

/** Presents a native chunk as the shape the core holds. */
function wrapChunk(chunk: EncodedChunkNative, decoderConfig: unknown): EncodedVideoChunkLike {
  return {
    type: chunk.type,
    timestampMicros: chunk.timestamp,
    durationMicros: chunk.duration,
    byteLength: chunk.byteLength,
    copyTo(destination: Uint8Array): void {
      chunk.copyTo(destination);
    },
    ...(decoderConfig === undefined ? {} : { decoderConfig }),
  };
}
